/**
 * Variation Order Payment Details — single commercial pricing authority.
 *
 * Equipment lines stay retail/list for scope composition.
 * Contract money (discount, net VO, amount due, finance action) is computed here.
 */

import {
  DTOOLS_DEFAULT_PAYMENT_TERMS,
  resolveDtoolsPaymentTerms,
  type DtoolsPortalPaymentTerm,
} from "./dtoolsDisplaySettings";
import {
  hasNegotiatedAgreedPrice,
  type RetailPricingJson,
} from "./proposalCustomerPricing";
import type { VoDeltaLine } from "./variationOrderDiff";
import { computeVoFinancialSnapshot, roundVoMoney } from "./variationOrderTypes";

export const VO_DISCOUNT_MODES = [
  "inherited_percentage",
  "custom_percentage",
  "fixed_amount",
  "no_discount",
] as const;
export type VoDiscountMode = (typeof VO_DISCOUNT_MODES)[number];

export const VO_FINANCE_ACTIONS = [
  "generate_deposit_invoice",
  "generate_credit_note",
  "no_finance_action",
] as const;
export type VoFinanceAction = (typeof VO_FINANCE_ACTIONS)[number];

export const VO_FINANCE_ACTION_LABELS: Record<VoFinanceAction, string> = {
  generate_deposit_invoice: "Generate Deposit Invoice",
  generate_credit_note: "Generate Credit Note",
  no_finance_action: "No Finance Action",
};

export const VO_CONFIRMATION_DEPOSIT_INCOMPLETE_MESSAGE =
  "Variation Orders require the original Confirmation / Deposit payment to be completed.";

export type VoOriginalContractContext = {
  retailValue: number;
  agreedContractValue: number;
  effectiveDiscountPct: number;
  paymentTerms: DtoolsPortalPaymentTerm[];
  confirmationPercentage: number;
};

export type VoPaymentDetailsSelection = {
  /** Checkbox: Apply original contract discount */
  applyOriginalDiscount: boolean;
  mode: VoDiscountMode;
  selectedPercentage: number;
  selectedFixedAmount: number;
};

export type VoPaymentDetailsComputed = {
  inheritedDiscountPct: number;
  addedRetailSubtotal: number;
  voDiscountAmount: number;
  voDiscountPct: number;
  addedContractValue: number;
  removedRetailSubtotal: number;
  removedContractCredit: number;
  currentVoValue: number;
  previouslyApprovedVosTotal: number;
  revisedContractValue: number;
  requiredDeposit: number;
  alreadyCollectedForRemoved: number;
  amountDueUponConfirmation: number;
  financeAction: VoFinanceAction;
  paymentTerms: DtoolsPortalPaymentTerm[];
  confirmationPercentage: number;
};

export type VoPaymentDetailsSnapshot = VoPaymentDetailsSelection &
  VoPaymentDetailsComputed & {
    original: VoOriginalContractContext;
    contractValueBeforeVo: number;
    /** Frozen when VO is approved — commercially immutable thereafter. */
    approvedAt?: string | null;
    /** Operational result of executing the frozen finance action after approval. */
    financeExecution?: {
      status: "not_required" | "completed" | "failed";
      action: string;
      amountSgd: number;
      attemptedAt: string;
      completedAt?: string;
      dealInvoiceId?: number;
      invoiceNumber?: string;
      instalmentStage?: string;
      paymentLink?: string | null;
      hitpayPaymentId?: string | null;
      linkAttempt?: {
        state:
          | "not_attempted"
          | "provider_confirmed_failure"
          | "provider_outcome_unknown"
          | "provider_request_recovered"
          | "link_persisted";
        referenceNumber: string;
        attemptedAt: string;
        hitpayPaymentId?: string | null;
        paymentLink?: string | null;
        error?: string;
        reconciliationRequired?: boolean;
        recovered?: boolean;
      } | null;
      error?: string;
    } | null;
  };

export type VoPaymentDetailsDraftInput = {
  applyOriginalDiscount?: boolean;
  mode?: VoDiscountMode;
  selectedPercentage?: number;
  selectedFixedAmount?: number;
};

function money(n: number): number {
  return roundVoMoney(n);
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function clampMoneyNonNeg(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, money(n));
}

export function isVoDiscountMode(value: unknown): value is VoDiscountMode {
  return VO_DISCOUNT_MODES.includes(String(value) as VoDiscountMode);
}

export function isVoFinanceAction(value: unknown): value is VoFinanceAction {
  return VO_FINANCE_ACTIONS.includes(String(value) as VoFinanceAction);
}

/** Confirmation tranche % from inherited payment terms (defaults to 50). */
export function resolveVoConfirmationPercentage(
  paymentTerms: DtoolsPortalPaymentTerm[] | null | undefined,
): number {
  const first = paymentTerms?.[0]?.percentage;
  const pct = Number(first);
  return Number.isFinite(pct) && pct > 0 ? pct : 50;
}

/**
 * Resolve the original accepted-contract commercial context.
 * Prefer a frozen inheritance snapshot when present (VO-002+).
 */
export function resolveOriginalContractCommercialContext(input: {
  totalAmount?: unknown;
  retailPricingJson?: RetailPricingJson | null;
  paymentTermsJson?: unknown;
  baselineRetailTotal?: number;
  frozen?: Partial<VoOriginalContractContext> | null;
}): VoOriginalContractContext {
  if (input.frozen && typeof input.frozen === "object") {
    const paymentTerms =
      Array.isArray(input.frozen.paymentTerms) && input.frozen.paymentTerms.length
        ? input.frozen.paymentTerms.map((t) => ({
            stage: String(t.stage ?? "").trim() || "Stage",
            percentage: moneyNumber(t.percentage),
            ...(t.fixedAmount != null ? { fixedAmount: moneyNumber(t.fixedAmount) } : {}),
          }))
        : [...DTOOLS_DEFAULT_PAYMENT_TERMS];
    const retailValue = clampMoneyNonNeg(moneyNumber(input.frozen.retailValue));
    const frozenAgreedContractValue = clampMoneyNonNeg(moneyNumber(input.frozen.agreedContractValue));
    const retail = input.retailPricingJson ?? null;
    const enteredGstInclusive =
      retail?.agreedPriceBasis === "gst_inclusive"
        ? clampMoneyNonNeg(moneyNumber(retail.enteredAgreedPriceSgd))
        : 0;
    const canonicalExGst = clampMoneyNonNeg(moneyNumber(retail?.agreedPriceSgd ?? retail?.agreedPrice));
    // Older VO snapshots could freeze the internal ex-GST accounting amount as
    // the customer contract value. When the proposal explicitly preserves the
    // staff-entered GST-inclusive agreement, that entered figure is authoritative
    // for customer-facing VO pricing.
    const replacesStaleCanonicalSnapshot =
      enteredGstInclusive > 0.004 &&
      canonicalExGst > 0.004 &&
      Math.abs(frozenAgreedContractValue - canonicalExGst) <= 0.02;
    const agreedContractValue = replacesStaleCanonicalSnapshot
      ? enteredGstInclusive
      : frozenAgreedContractValue;
    const effectiveDiscountPct =
      replacesStaleCanonicalSnapshot && retailValue > 0
        ? clampPct(((retailValue - agreedContractValue) / retailValue) * 100)
        : input.frozen.effectiveDiscountPct != null
        ? clampPct(moneyNumber(input.frozen.effectiveDiscountPct))
        : retailValue > 0
          ? clampPct(((retailValue - agreedContractValue) / retailValue) * 100)
          : 0;
    return {
      retailValue,
      agreedContractValue,
      effectiveDiscountPct,
      paymentTerms,
      confirmationPercentage: resolveVoConfirmationPercentage(paymentTerms),
    };
  }

  const retail = input.retailPricingJson ?? null;
  const paymentTerms = resolveDtoolsPaymentTerms(input.paymentTermsJson);
  const fromRetail = moneyNumber(retail?.listTotal ?? retail?.originalListPrice);
  const baseline = clampMoneyNonNeg(input.baselineRetailTotal ?? 0);
  const retailValue = fromRetail > 0 ? money(fromRetail) : baseline;
  const negotiated = hasNegotiatedAgreedPrice(retail);
  const agreedRaw = negotiated
    ? retail?.agreedPriceBasis === "gst_inclusive" && moneyNumber(retail?.enteredAgreedPriceSgd) > 0
      ? moneyNumber(retail?.enteredAgreedPriceSgd)
      : moneyNumber(retail?.agreedPriceSgd ?? retail?.agreedPrice)
    : moneyNumber(input.totalAmount);
  const agreedContractValue =
    agreedRaw > 0 ? money(agreedRaw) : retailValue > 0 ? retailValue : money(moneyNumber(input.totalAmount));
  const effectiveDiscountPct =
    retailValue > 0 ? clampPct(((retailValue - agreedContractValue) / retailValue) * 100) : 0;

  return {
    retailValue,
    agreedContractValue,
    effectiveDiscountPct,
    paymentTerms: paymentTerms.length ? paymentTerms : [...DTOOLS_DEFAULT_PAYMENT_TERMS],
    confirmationPercentage: resolveVoConfirmationPercentage(paymentTerms),
  };
}

export function resolveVoDraftContractValueBefore(input: {
  storedContractValueBeforeVo?: unknown;
  canonicalHeaderTotal?: unknown;
  originalAgreedContractValue: number;
  previouslyApprovedVosTotal?: unknown;
}): number {
  const stored = money(moneyNumber(input.storedContractValueBeforeVo));
  const canonical = money(moneyNumber(input.canonicalHeaderTotal));
  const originalAgreed = money(moneyNumber(input.originalAgreedContractValue));
  const priorVos = money(moneyNumber(input.previouslyApprovedVosTotal));
  if (
    priorVos <= 0.004 &&
    originalAgreed > 0.004 &&
    Math.abs(stored - canonical) <= 0.02 &&
    Math.abs(originalAgreed - canonical) > 0.02
  ) {
    return originalAgreed;
  }
  return stored > 0.004 ? stored : originalAgreed;
}

/** Split retail deltas into added vs removed buckets (qty± included). */
export function splitVoRetailBuckets(
  lines: Array<{ changeType: string; lineTotalDelta: number }>,
): { addedRetail: number; removedRetail: number } {
  let addedRetail = 0;
  let removedRetail = 0;
  for (const line of lines) {
    const delta = moneyNumber(line.lineTotalDelta);
    if (line.changeType === "removed") {
      removedRetail += Math.abs(delta);
      continue;
    }
    if (line.changeType === "added") {
      addedRetail += Math.max(0, delta);
      continue;
    }
    if (line.changeType === "quantity_changed") {
      if (delta < 0) removedRetail += Math.abs(delta);
      else if (delta > 0) addedRetail += delta;
    }
  }
  return {
    addedRetail: money(addedRetail),
    removedRetail: money(removedRetail),
  };
}

export function defaultVoPaymentDetailsSelection(
  inheritedDiscountPct: number,
): VoPaymentDetailsSelection {
  const pct = clampPct(inheritedDiscountPct);
  return {
    applyOriginalDiscount: pct > 0.004,
    mode: pct > 0.004 ? "inherited_percentage" : "no_discount",
    selectedPercentage: pct,
    selectedFixedAmount: 0,
  };
}

export function normalizeVoPaymentDetailsSelection(
  raw: VoPaymentDetailsDraftInput | null | undefined,
  inheritedDiscountPct: number,
  addedRetail = 0,
): VoPaymentDetailsSelection {
  const inherited = clampPct(inheritedDiscountPct);
  if (!raw || typeof raw !== "object") {
    return defaultVoPaymentDetailsSelection(inherited);
  }

  const applyOriginalDiscount =
    raw.applyOriginalDiscount == null
      ? inherited > 0.004
      : Boolean(raw.applyOriginalDiscount);

  if (!applyOriginalDiscount) {
    return {
      applyOriginalDiscount: false,
      mode: "no_discount",
      selectedPercentage: 0,
      selectedFixedAmount: 0,
    };
  }

  let mode: VoDiscountMode = isVoDiscountMode(raw.mode) ? raw.mode : "inherited_percentage";
  if (mode === "no_discount") mode = "inherited_percentage";

  let selectedPercentage =
    raw.selectedPercentage != null ? clampPct(moneyNumber(raw.selectedPercentage)) : inherited;
  let selectedFixedAmount =
    raw.selectedFixedAmount != null ? clampMoneyNonNeg(moneyNumber(raw.selectedFixedAmount)) : 0;

  if (mode === "inherited_percentage") {
    selectedPercentage = inherited;
    selectedFixedAmount = money((addedRetail * selectedPercentage) / 100);
  } else if (mode === "custom_percentage") {
    selectedFixedAmount = money((addedRetail * selectedPercentage) / 100);
  } else if (mode === "fixed_amount") {
    selectedPercentage =
      addedRetail > 0.004 ? clampPct((selectedFixedAmount / addedRetail) * 100) : 0;
  }

  return {
    applyOriginalDiscount: true,
    mode,
    selectedPercentage,
    selectedFixedAmount,
  };
}

/** Checkbox toggled — resets to inherited % or clears discount. */
export function setVoApplyOriginalDiscount(
  checked: boolean,
  inheritedDiscountPct: number,
  addedRetail: number,
): VoPaymentDetailsSelection {
  if (!checked) {
    return {
      applyOriginalDiscount: false,
      mode: "no_discount",
      selectedPercentage: 0,
      selectedFixedAmount: 0,
    };
  }
  const pct = clampPct(inheritedDiscountPct);
  return {
    applyOriginalDiscount: true,
    mode: "inherited_percentage",
    selectedPercentage: pct,
    selectedFixedAmount: money((addedRetail * pct) / 100),
  };
}

/** Percentage is the sole authority — updates fixed amount. */
export function setVoDiscountPercentage(
  percentage: number,
  inheritedDiscountPct: number,
  addedRetail: number,
): VoPaymentDetailsSelection {
  const pct = clampPct(percentage);
  const inherited = clampPct(inheritedDiscountPct);
  const mode: VoDiscountMode =
    Math.abs(pct - inherited) < 0.004 ? "inherited_percentage" : "custom_percentage";
  return {
    applyOriginalDiscount: true,
    mode,
    selectedPercentage: pct,
    selectedFixedAmount: money((addedRetail * pct) / 100),
  };
}

/** Fixed amount is the sole authority — updates percentage. */
export function setVoDiscountFixedAmount(
  fixedAmount: number,
  addedRetail: number,
): VoPaymentDetailsSelection {
  const amount = clampMoneyNonNeg(fixedAmount);
  const pct = addedRetail > 0.004 ? clampPct((amount / addedRetail) * 100) : 0;
  return {
    applyOriginalDiscount: true,
    mode: "fixed_amount",
    selectedPercentage: pct,
    selectedFixedAmount: amount,
  };
}

function resolveAppliedVoDiscount(input: {
  selection: VoPaymentDetailsSelection;
  inheritedDiscountPct: number;
  addedRetail: number;
}): { voDiscountPct: number; voDiscountAmount: number; mode: VoDiscountMode } {
  const selection = normalizeVoPaymentDetailsSelection(
    input.selection,
    input.inheritedDiscountPct,
    input.addedRetail,
  );
  if (!selection.applyOriginalDiscount || selection.mode === "no_discount") {
    return { voDiscountPct: 0, voDiscountAmount: 0, mode: "no_discount" };
  }
  if (selection.mode === "fixed_amount") {
    const voDiscountAmount = Math.min(
      clampMoneyNonNeg(selection.selectedFixedAmount),
      clampMoneyNonNeg(input.addedRetail),
    );
    const voDiscountPct =
      input.addedRetail > 0.004 ? clampPct((voDiscountAmount / input.addedRetail) * 100) : 0;
    return { voDiscountPct, voDiscountAmount, mode: "fixed_amount" };
  }
  const voDiscountPct =
    selection.mode === "inherited_percentage"
      ? clampPct(input.inheritedDiscountPct)
      : clampPct(selection.selectedPercentage);
  const voDiscountAmount = money((input.addedRetail * voDiscountPct) / 100);
  return { voDiscountPct, voDiscountAmount, mode: selection.mode };
}

export function resolveVoFinanceAction(amountDueUponConfirmation: number): VoFinanceAction {
  const due = money(amountDueUponConfirmation);
  if (due > 0.004) return "generate_deposit_invoice";
  if (due < -0.004) return "generate_credit_note";
  return "no_finance_action";
}

export function computeVoPaymentDetails(input: {
  original: VoOriginalContractContext;
  selection: VoPaymentDetailsDraftInput | VoPaymentDetailsSelection;
  addedRetail: number;
  removedRetail: number;
  contractValueBeforeVo: number;
  previouslyApprovedVosTotal?: number;
}): VoPaymentDetailsSnapshot {
  const original = input.original;
  const addedRetail = clampMoneyNonNeg(input.addedRetail);
  const removedRetail = clampMoneyNonNeg(input.removedRetail);
  const selection = normalizeVoPaymentDetailsSelection(
    input.selection,
    original.effectiveDiscountPct,
    addedRetail,
  );
  const applied = resolveAppliedVoDiscount({
    selection,
    inheritedDiscountPct: original.effectiveDiscountPct,
    addedRetail,
  });

  const originalFactor = 1 - original.effectiveDiscountPct / 100;
  const removedContractCredit = money(removedRetail * Math.max(0, originalFactor));
  const addedContractValue = money(addedRetail - applied.voDiscountAmount);
  const currentVoValue = money(addedContractValue - removedContractCredit);
  const contractValueBeforeVo = money(input.contractValueBeforeVo);
  const previouslyApprovedVosTotal = money(input.previouslyApprovedVosTotal ?? 0);
  const financials = computeVoFinancialSnapshot({
    contractValueBeforeVo,
    variationAmount: currentVoValue,
  });

  const confirmationPercentage = original.confirmationPercentage;
  /**
   * Amount Due Upon Confirmation — commercial rule (authoritative):
   *
   *   requiredDeposit              = confirmation% × addedContractValue
   *   alreadyCollectedForRemoved   = confirmation% × removedContractCredit
   *   amountDueUponConfirmation    = requiredDeposit − alreadyCollectedForRemoved
   *
   * Exact-arithmetic identity with confirmation% × currentVoValue:
   *
   *   required − already
   *     = (p/100)×added − (p/100)×removed
   *     = (p/100)×(added − removed)
   *     = (p/100)×currentVoValue
   *
   * With per-step SGD rounding (`roundVoMoney` on each term), the two forms can
   * diverge by ±$0.01. We therefore compute the commercial subtraction explicitly
   * and never shortcut through currentVoValue.
   */
  const requiredDeposit = money((addedContractValue * confirmationPercentage) / 100);
  const alreadyCollectedForRemoved = money((removedContractCredit * confirmationPercentage) / 100);
  const amountDueUponConfirmation = money(requiredDeposit - alreadyCollectedForRemoved);
  const financeAction = resolveVoFinanceAction(amountDueUponConfirmation);

  return {
    original,
    applyOriginalDiscount: selection.applyOriginalDiscount,
    mode: applied.mode,
    selectedPercentage: applied.voDiscountPct,
    selectedFixedAmount: applied.voDiscountAmount,
    inheritedDiscountPct: original.effectiveDiscountPct,
    addedRetailSubtotal: addedRetail,
    voDiscountAmount: applied.voDiscountAmount,
    voDiscountPct: applied.voDiscountPct,
    addedContractValue,
    removedRetailSubtotal: removedRetail,
    removedContractCredit,
    currentVoValue,
    previouslyApprovedVosTotal,
    revisedContractValue: financials.revisedContractValue,
    contractValueBeforeVo: financials.originalContractValue,
    requiredDeposit,
    alreadyCollectedForRemoved,
    amountDueUponConfirmation,
    financeAction,
    paymentTerms: original.paymentTerms,
    confirmationPercentage,
  };
}

export function computeVoPaymentDetailsFromDeltaLines(input: {
  original: VoOriginalContractContext;
  selection: VoPaymentDetailsDraftInput | VoPaymentDetailsSelection;
  lines: VoDeltaLine[] | Array<{ changeType: string; lineTotalDelta: number }>;
  contractValueBeforeVo: number;
  previouslyApprovedVosTotal?: number;
}): VoPaymentDetailsSnapshot {
  const buckets = splitVoRetailBuckets(input.lines);
  return computeVoPaymentDetails({
    original: input.original,
    selection: input.selection,
    addedRetail: buckets.addedRetail,
    removedRetail: buckets.removedRetail,
    contractValueBeforeVo: input.contractValueBeforeVo,
    previouslyApprovedVosTotal: input.previouslyApprovedVosTotal,
  });
}

export type VoPaymentDetailsValidationIssue = {
  code:
    | "negative_revised_contract"
    | "conflicting_discount_mode"
    | "inconsistent_amount_due"
    | "inconsistent_finance_action"
    | "discount_exceeds_added_retail";
  message: string;
};

export function validateVoPaymentDetails(
  snapshot: VoPaymentDetailsSnapshot,
): VoPaymentDetailsValidationIssue[] {
  const issues: VoPaymentDetailsValidationIssue[] = [];
  if (snapshot.revisedContractValue < -0.004) {
    issues.push({
      code: "negative_revised_contract",
      message: "Revised contract value cannot be negative.",
    });
  }
  if (
    snapshot.applyOriginalDiscount &&
    snapshot.mode === "no_discount" &&
    snapshot.voDiscountAmount > 0.004
  ) {
    issues.push({
      code: "conflicting_discount_mode",
      message: "Discount mode conflict: no-discount mode cannot carry a discount amount.",
    });
  }
  if (!snapshot.applyOriginalDiscount && snapshot.mode !== "no_discount") {
    issues.push({
      code: "conflicting_discount_mode",
      message: "Discount mode conflict: unchecked Apply original discount must use no_discount.",
    });
  }
  if (snapshot.voDiscountAmount - snapshot.addedRetailSubtotal > 0.004) {
    issues.push({
      code: "discount_exceeds_added_retail",
      message: "VO discount cannot exceed added retail value.",
    });
  }
  const expectedDue = money(
    snapshot.requiredDeposit - snapshot.alreadyCollectedForRemoved,
  );
  if (Math.abs(expectedDue - snapshot.amountDueUponConfirmation) > 0.004) {
    issues.push({
      code: "inconsistent_amount_due",
      message: "Amount due upon confirmation is inconsistent with payment calculation.",
    });
  }
  const expectedAction = resolveVoFinanceAction(snapshot.amountDueUponConfirmation);
  if (expectedAction !== snapshot.financeAction) {
    issues.push({
      code: "inconsistent_finance_action",
      message: "Expected finance action does not match amount due.",
    });
  }
  return issues;
}

export function parseVoPaymentDetailsFromDraft(
  draftWorkspaceJson: unknown,
): {
  selection: VoPaymentDetailsDraftInput | null;
  original: Partial<VoOriginalContractContext> | null;
  snapshot: VoPaymentDetailsSnapshot | null;
} {
  if (!draftWorkspaceJson || typeof draftWorkspaceJson !== "object" || Array.isArray(draftWorkspaceJson)) {
    return { selection: null, original: null, snapshot: null };
  }
  const root = draftWorkspaceJson as Record<string, unknown>;
  const payment = root.paymentDetails;
  if (!payment || typeof payment !== "object" || Array.isArray(payment)) {
    return { selection: null, original: null, snapshot: null };
  }
  const p = payment as Record<string, unknown>;
  const selection: VoPaymentDetailsDraftInput = {
    applyOriginalDiscount:
      p.applyOriginalDiscount == null ? undefined : Boolean(p.applyOriginalDiscount),
    mode: isVoDiscountMode(p.mode) ? p.mode : undefined,
    selectedPercentage: p.selectedPercentage == null ? undefined : moneyNumber(p.selectedPercentage),
    selectedFixedAmount: p.selectedFixedAmount == null ? undefined : moneyNumber(p.selectedFixedAmount),
  };
  const original =
    p.original && typeof p.original === "object" && !Array.isArray(p.original)
      ? (p.original as Partial<VoOriginalContractContext>)
      : null;
  const snapshot = isCompleteVoPaymentDetailsSnapshot(payment) ? payment : null;
  return { selection, original, snapshot };
}

/** True when a persisted object has the commercial fields required for document rendering. */
export function isCompleteVoPaymentDetailsSnapshot(
  value: unknown,
): value is VoPaymentDetailsSnapshot {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const p = value as Record<string, unknown>;
  if (p.revisedContractValue == null || p.currentVoValue == null) return false;
  if (p.addedContractValue == null || p.amountDueUponConfirmation == null) return false;
  if (p.addedRetailSubtotal == null || p.voDiscountAmount == null) return false;
  if (!isVoFinanceAction(p.financeAction)) return false;
  if (!p.original || typeof p.original !== "object" || Array.isArray(p.original)) return false;
  return true;
}

function selectionFromSnapshot(
  snapshot: VoPaymentDetailsSnapshot,
): VoPaymentDetailsSelection {
  return {
    applyOriginalDiscount: Boolean(snapshot.applyOriginalDiscount),
    mode: isVoDiscountMode(snapshot.mode) ? snapshot.mode : "no_discount",
    selectedPercentage: moneyNumber(snapshot.selectedPercentage),
    selectedFixedAmount: moneyNumber(snapshot.selectedFixedAmount),
  };
}

/**
 * Resolve the Payment Details snapshot for VO document surfaces (Link A/B, print).
 *
 * - Approved: freeze the complete persisted snapshot (never recompute).
 * - Draft/sent: recompute from frozen original + selection + lines when possible
 *   (same authority as saveDraft / the editing workspace).
 * - `fallbackOriginal` supplies proposal-header commercial context when the VO
 *   record does not yet carry a frozen `original` (matches live editor behaviour).
 */
export function resolveVoDocumentPaymentDetails(input: {
  paymentDetailsJson?: unknown;
  draftWorkspaceJson?: unknown;
  lines: Array<{ changeType: string; lineTotalDelta: number }>;
  contractValueBeforeVo: number;
  previouslyApprovedVosTotal?: number;
  /** When true, never recompute — approved commercial freeze. */
  freezeSnapshot?: boolean;
  /** Proposal-header / inheritance original when VO storage lacks `original`. */
  fallbackOriginal?: VoOriginalContractContext | null;
}): VoPaymentDetailsSnapshot | null {
  const fromCol = isCompleteVoPaymentDetailsSnapshot(input.paymentDetailsJson)
    ? input.paymentDetailsJson
    : null;
  const parsed = parseVoPaymentDetailsFromDraft(input.draftWorkspaceJson);
  const fromDraft = parsed.snapshot;
  const preferred = fromCol ?? fromDraft;

  if (input.freezeSnapshot) {
    return preferred;
  }

  const originalPartial = preferred?.original ?? parsed.original;
  const original = originalPartial
    ? resolveOriginalContractCommercialContext({ frozen: originalPartial })
    : input.fallbackOriginal ?? null;
  const selection: VoPaymentDetailsDraftInput | VoPaymentDetailsSelection | null = preferred
    ? selectionFromSnapshot(preferred)
    : parsed.selection ??
      (original ? defaultVoPaymentDetailsSelection(original.effectiveDiscountPct) : null);

  if (original && selection) {
    const contractValueBeforeVo =
      moneyNumber(input.contractValueBeforeVo) > 0.004
        ? money(input.contractValueBeforeVo)
        : money(preferred?.contractValueBeforeVo ?? original.agreedContractValue);
    const previouslyApprovedVosTotal =
      input.previouslyApprovedVosTotal != null
        ? money(input.previouslyApprovedVosTotal)
        : money(preferred?.previouslyApprovedVosTotal ?? 0);
    return computeVoPaymentDetailsFromDeltaLines({
      original,
      selection,
      lines: input.lines,
      contractValueBeforeVo,
      previouslyApprovedVosTotal,
    });
  }

  return preferred;
}

/**
 * Historical pre-commercial VO: no paymentDetails column payload and no draft
 * paymentDetails object. Only these may use the legacy retail Variation Order Summary.
 */
export function isHistoricalPreCommercialVo(input: {
  paymentDetailsJson?: unknown;
  draftWorkspaceJson?: unknown;
}): boolean {
  if (input.paymentDetailsJson != null && typeof input.paymentDetailsJson === "object") {
    return false;
  }
  if (
    !input.draftWorkspaceJson ||
    typeof input.draftWorkspaceJson !== "object" ||
    Array.isArray(input.draftWorkspaceJson)
  ) {
    return true;
  }
  const payment = (input.draftWorkspaceJson as Record<string, unknown>).paymentDetails;
  return payment == null || typeof payment !== "object" || Array.isArray(payment);
}

export function withVoPaymentDetailsInDraft(
  draftWorkspaceJson: Record<string, unknown> | null | undefined,
  snapshot: VoPaymentDetailsSnapshot,
): Record<string, unknown> {
  const base =
    draftWorkspaceJson && typeof draftWorkspaceJson === "object" && !Array.isArray(draftWorkspaceJson)
      ? { ...draftWorkspaceJson }
      : {};
  return {
    ...base,
    paymentDetails: {
      original: snapshot.original,
      applyOriginalDiscount: snapshot.applyOriginalDiscount,
      mode: snapshot.mode,
      selectedPercentage: snapshot.selectedPercentage,
      selectedFixedAmount: snapshot.selectedFixedAmount,
      inheritedDiscountPct: snapshot.inheritedDiscountPct,
      addedRetailSubtotal: snapshot.addedRetailSubtotal,
      voDiscountAmount: snapshot.voDiscountAmount,
      voDiscountPct: snapshot.voDiscountPct,
      addedContractValue: snapshot.addedContractValue,
      removedRetailSubtotal: snapshot.removedRetailSubtotal,
      removedContractCredit: snapshot.removedContractCredit,
      currentVoValue: snapshot.currentVoValue,
      previouslyApprovedVosTotal: snapshot.previouslyApprovedVosTotal,
      revisedContractValue: snapshot.revisedContractValue,
      contractValueBeforeVo: snapshot.contractValueBeforeVo,
      requiredDeposit: snapshot.requiredDeposit,
      alreadyCollectedForRemoved: snapshot.alreadyCollectedForRemoved,
      amountDueUponConfirmation: snapshot.amountDueUponConfirmation,
      financeAction: snapshot.financeAction,
      paymentTerms: snapshot.paymentTerms,
      confirmationPercentage: snapshot.confirmationPercentage,
      ...(snapshot.approvedAt ? { approvedAt: snapshot.approvedAt } : {}),
    },
  };
}

/** Detect discount authority changes for audit. */
export function voDiscountSelectionChanged(
  previous: Pick<
    VoPaymentDetailsSelection,
    "applyOriginalDiscount" | "mode" | "selectedPercentage" | "selectedFixedAmount"
  > | null
    | undefined,
  next: Pick<
    VoPaymentDetailsSelection,
    "applyOriginalDiscount" | "mode" | "selectedPercentage" | "selectedFixedAmount"
  >,
): boolean {
  if (!previous) return true;
  return (
    Boolean(previous.applyOriginalDiscount) !== Boolean(next.applyOriginalDiscount) ||
    String(previous.mode) !== String(next.mode) ||
    Math.abs(moneyNumber(previous.selectedPercentage) - moneyNumber(next.selectedPercentage)) >
      0.004 ||
    Math.abs(moneyNumber(previous.selectedFixedAmount) - moneyNumber(next.selectedFixedAmount)) >
      0.004
  );
}

export function formatVoFinanceActionLabel(action: VoFinanceAction | string | null | undefined): string {
  if (isVoFinanceAction(action)) return VO_FINANCE_ACTION_LABELS[action];
  return String(action ?? "—");
}

/**
 * Customer/document presentation of the original proposal commercial triad.
 *
 * - originalContractValue = pre-discount retail/header total (`original.retailValue`)
 * - originalEffectiveDiscountPct = effective discount %
 * - originalAgreedContractValue = negotiated/agreed contract total
 *
 * Never map `contractValueBeforeVo` or `agreedContractValue` into Original Contract Value.
 */
export type VoOriginalContractPresentation = {
  originalContractValue: number;
  originalEffectiveDiscountPct: number;
  originalAgreedContractValue: number;
};

export function resolveVoOriginalContractPresentation(
  snapshot: Pick<VoPaymentDetailsSnapshot, "original" | "inheritedDiscountPct">,
): VoOriginalContractPresentation {
  const originalContractValue = money(moneyNumber(snapshot.original?.retailValue));
  const originalAgreedContractValue = money(moneyNumber(snapshot.original?.agreedContractValue));
  const originalEffectiveDiscountPct =
    snapshot.original?.effectiveDiscountPct != null
      ? clampPct(moneyNumber(snapshot.original.effectiveDiscountPct))
      : clampPct(moneyNumber(snapshot.inheritedDiscountPct));
  return {
    originalContractValue,
    originalEffectiveDiscountPct,
    originalAgreedContractValue,
  };
}

/**
 * Soft reconcile check: agreed ≈ retail × (1 − discount%).
 * Returns null when inputs are incomplete; otherwise the absolute delta in SGD.
 */
export function voOriginalContractReconcileDelta(
  presentation: VoOriginalContractPresentation,
): number | null {
  const retail = presentation.originalContractValue;
  const agreed = presentation.originalAgreedContractValue;
  const pct = presentation.originalEffectiveDiscountPct;
  if (retail < 0.004) return null;
  const expected = money(retail * (1 - pct / 100));
  return money(Math.abs(expected - agreed));
}

export function voOriginalContractReconciles(
  presentation: VoOriginalContractPresentation,
  toleranceSgd = 0.05,
): boolean {
  const delta = voOriginalContractReconcileDelta(presentation);
  if (delta == null) return false;
  // Allow for SGD cent rounding plus 2-decimal display of effectiveDiscountPct
  // (0.01pp of retail ≈ half a basis-point band on large contracts).
  const pctDisplayBand = money(presentation.originalContractValue * 0.00005);
  return delta <= Math.max(toleranceSgd, pctDisplayBand) + 0.0001;
}


/**
 * Map retail line deltas to commercial document amounts from the Payment Details
 * snapshot. Does not recompute VO totals — only allocates the snapshot’s contract
 * factors so item Amounts reconcile with Added Contract Value / Removed Contract Credit.
 */
export function mapVoDocumentLineCommercialAmounts(
  lines: Array<{ stableLineKey: string; changeType: string; lineTotalDelta: number }>,
  payment: Pick<
    VoPaymentDetailsSnapshot,
    | "inheritedDiscountPct"
    | "voDiscountPct"
    | "voDiscountAmount"
    | "mode"
    | "addedRetailSubtotal"
    | "addedContractValue"
    | "removedContractCredit"
  >,
): Record<string, number> {
  const originalFactor = Math.max(0, 1 - clampPct(payment.inheritedDiscountPct) / 100);
  const addedFactor = Math.max(0, 1 - clampPct(payment.voDiscountPct) / 100);
  const addedRetailTotal = clampMoneyNonNeg(payment.addedRetailSubtotal);
  const useFixedShare =
    payment.mode === "fixed_amount" &&
    payment.voDiscountAmount > 0.004 &&
    addedRetailTotal > 0.004;

  const out: Record<string, number> = {};
  const addedKeys: string[] = [];
  const removedKeys: string[] = [];

  for (const line of lines) {
    const retail = moneyNumber(line.lineTotalDelta);
    const isRemoved =
      line.changeType === "removed" || (line.changeType === "quantity_changed" && retail < -0.004);
    const isAdded =
      line.changeType === "added" || (line.changeType === "quantity_changed" && retail > 0.004);

    if (isRemoved) {
      const credit = money(Math.abs(retail) * originalFactor);
      out[line.stableLineKey] = money(-credit);
      removedKeys.push(line.stableLineKey);
      continue;
    }
    if (isAdded) {
      const absRetail = Math.abs(retail);
      let commercial: number;
      if (useFixedShare) {
        const share = absRetail / addedRetailTotal;
        commercial = money(absRetail - payment.voDiscountAmount * share);
      } else {
        commercial = money(absRetail * addedFactor);
      }
      out[line.stableLineKey] = commercial;
      addedKeys.push(line.stableLineKey);
      continue;
    }
    out[line.stableLineKey] = money(retail);
  }

  // Residual so line commercial sums match the snapshot buckets (display reconciliation).
  if (addedKeys.length > 0) {
    const sum = money(addedKeys.reduce((acc, key) => acc + moneyNumber(out[key]), 0));
    const target = money(payment.addedContractValue);
    const last = addedKeys[addedKeys.length - 1]!;
    out[last] = money(moneyNumber(out[last]) + (target - sum));
  }
  if (removedKeys.length > 0) {
    const sumAbs = money(
      removedKeys.reduce((acc, key) => acc + Math.abs(moneyNumber(out[key])), 0),
    );
    const target = money(payment.removedContractCredit);
    const last = removedKeys[removedKeys.length - 1]!;
    const adjustedAbs = money(Math.abs(moneyNumber(out[last])) + (target - sumAbs));
    out[last] = money(-adjustedAbs);
  }

  return out;
}

export function customerAmountDueMessage(amountDueUponConfirmation: number): string {
  const due = money(amountDueUponConfirmation);
  if (Math.abs(due) < 0.004) return "No payment required upon confirmation.";
  if (due < 0) {
    return `A credit note of $${Math.abs(due).toLocaleString("en-SG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} will apply against future invoices. No refund will be issued.`;
  }
  return `Amount due upon confirmation of this VO: $${due.toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
